import {
  DepressionContentPage,
  FAQItem,
} from "@/components/DepressionContentPage";
import { Link } from "@/lib/router-compat";
import { RelatedLink } from "@/components/RelatedPages";
import { SaintComparisonVisualSplit } from "@/components/SaintComparisonInline";

const faqs: FAQItem[] = [
  {
    question: "Is depression genetic?",
    answer:
      "Depression does run in families. If a first-degree relative has had major depression, your own risk is roughly two to three times higher. Heritability estimates for major depression sit around 35 to 40 percent, meaning a substantial share of the variation in who gets depressed is explained by genes. But that leaves 60 percent to everything else: life stress, sleep, illness, loss, and relationships. Genes load the gun; they don't pull the trigger.",
  },
  {
    question:
      "If depression runs in my family, will treatment still work for me?",
    answer:
      "Yes. There is no gene that makes someone immune to therapy, medication, or brain stimulation. People with a strong family history often respond to treatment just as well as anyone else; they simply had a higher chance of needing it in the first place. Your family history explains why you got depressed. It doesn't get to decide how far you recover.",
  },
  {
    question: "Will my depression follow the same course as my parent's?",
    answer:
      "Not necessarily. Your mother's or father's depression, in a different decade with different treatments available, is not a preview of yours. The origin of your depression doesn't change the fact that it's an illness with treatments, and your course may look very different from a relative's.",
  },
  {
    question:
      "Why does inherited depression often respond only partially to medication?",
    answer:
      "Depression that runs strongly in families tends to be the recurrent, biologically driven kind that points to something happening in the brain's wiring, not just its chemistry. Antidepressants change the chemical environment around the underactive mood-regulation circuit. They don't directly retrain the circuit itself, which is why someone with a strong family history can be on a high dose of a good medication and still feel fundamentally unchanged.",
  },
  {
    question:
      "How is SAINT different from standard TMS for inherited depression?",
    answer:
      "Standard TMS locates its target using external measurements of the head. SAINT uses a functional MRI of your own brain to identify the precise spot where stimulation will most effectively reach the mood-regulation network. Because no two brains are wired the same way, even within a family, SAINT's personalized targeting may be especially relevant for inherited, recurrent depression.",
  },
  {
    question: "Do I have to stop my medication to do SAINT?",
    answer:
      "No. Most patients continue their existing medication regimen through treatment. SAINT is not a replacement for what you've built — it is a different kind of intervention aimed at the brain circuitry your medications may not have reached. Never change medication without guidance from your prescribing provider.",
  },
];

const related: RelatedLink[] = [
  {
    title: "You've Tried Everything. Why Is the Depression Still Here?",
    href: "/youve-tried-everything-why-is-the-depression-still-here",
    description:
      "What treatment-resistant depression means and why medications don't work for everyone.",
  },
  {
    title:
      "Depressed and Anxious at the Same Time? You're Not Confused. You're Typical.",
    href: "/depressed-and-anxious-at-the-same-time",
    description:
      "Why depression and anxiety overlap and how targeting shared brain circuits may help.",
  },
  {
    title: "Am I a Candidate for TMS?",
    href: "/am-i-a-candidate",
    description: "What clinicians consider when evaluating TMS eligibility.",
  },
  {
    title: "What Is SAINT TMS Therapy?",
    href: "/learn/what-is-saint-tms",
    description: "How the five-day, fMRI-guided protocol works.",
  },
];

export default function ItRunsInMyFamily() {
  return (
    <DepressionContentPage
      seo={{
        title:
          '"It Runs in My Family" — What That Means for Depression | Neuro Wellness',
        description:
          "Depression runs in families, but family history doesn't mean treatment won't work. Learn what heritability really predicts and how SAINT TMS targets the brain circuits involved.",
        canonical: "/it-runs-in-my-family-what-that-means-for-your-depression",
        keywords:
          "is depression genetic, depression runs in family, heritability of depression, inherited depression treatment, SAINT TMS, treatment-resistant depression, family history depression",
      }}
      badge="Clinical Insights"
      heroTitle='"It Runs in My Family." What That Actually Means for Your Depression'
      heroSubtitle="It's often said with a shrug, as if the diagnosis came pre-installed and there's not much to do about it. That belief is half right and half wrong — and the wrong half can keep you stuck for years."
      lastUpdated="2026-11-03"
      sections={[
        {
          heading: "What family history really predicts",
          body: (
            <div className="space-y-4">
              <p>
                Somewhere in your life you've said it out loud: My mom has
                depression. My sister has it too. My dad's a worrier, always has
                been. It runs in the family.
              </p>
              <p>
                It's often said with a shrug, as if the diagnosis came
                pre-installed and there's not much to do about it. And there's a
                quieter version of the same thought that people don't say out
                loud: If it's genetic, maybe treatment won't really work. Maybe
                this is just who I am.
              </p>
              <p>
                That belief is worth examining, because it's half right and half
                wrong, and the wrong half can keep you stuck for years.
              </p>
              <p>
                Depression does run in families. If a first-degree relative (a
                parent or sibling) has had major depression, your own risk is
                roughly two to three times higher than someone with no family
                history. Anxiety disorders and obsessive-compulsive disorder
                cluster in families too, and they often show up together across
                generations: depression in one relative, generalized anxiety in
                another, OCD in a third.
              </p>
              <p>
                So the "it runs in the family" part is accurate. Heritability
                estimates for major depression sit around 35 to 40 percent,
                meaning a substantial share of the variation in who gets
                depressed is explained by genes.
              </p>
              <p>
                But here's what people miss. A 40 percent heritability leaves 60
                percent to everything else: life stress, sleep, illness, loss,
                relationships, and the ordinary accumulation of hard years.
                Genes load the gun. They don't pull the trigger, and they
                certainly don't decide whether treatment works.
              </p>

              {/* Infographic: Genes are not destiny */}
              <div className="my-6 overflow-hidden rounded-2xl border border-border bg-card shadow-soft not-prose">
                <img
                  src="https://vibe.filesafe.space/1787327131284421342/attachments/87e97998-22ff-4402-9db0-284bc47f9bf5.png"
                  alt="Genes are not destiny: Genetic vulnerability vs environment & life experiences in shaping individual mental health"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          ),
        },
        {
          heading: "What family history does not predict",
          body: (
            <div className="space-y-4">
              <p>
                It does not predict that your depression is untreatable. There's
                no gene that makes someone immune to therapy, medication, or
                brain stimulation. People with a strong family history often
                respond to treatment just as well as anyone else; they simply
                had a higher chance of needing it in the first place.
              </p>
              <p>
                It does not predict that you'll have the same course your parent
                did. Your mother's depression at forty, in a different decade
                with different treatments available, is not a preview of yours.
              </p>
              <p>
                And it does not mean the sadness is somehow more "real" or more
                permanent than depression that arrives without a family pattern.
                Depression is depression. The origin doesn't change the fact
                that it's an illness with treatments.
              </p>

              {/* Infographic: What family history can tell you */}
              <div className="my-6 overflow-hidden rounded-2xl border border-border bg-card shadow-soft not-prose">
                <img
                  src="https://vibe.filesafe.space/1787327131284421342/attachments/4bad3e39-bb2b-4173-a209-aa25d896edad.png"
                  alt="What family history can tell you: Earlier awareness, watch for patterns, share it with your provider, seek help sooner"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          ),
        },
        {
          heading: "Why the belief is so sticky",
          body: (
            <div className="space-y-4">
              <p>
                There's something almost comforting about the genetic
                explanation. It removes blame. It gives the suffering a reason.
                If you grew up watching a parent struggle, you may have absorbed
                the idea early that this is simply how your family is built, and
                that the goal is to manage, not to recover.
              </p>
              <p>
                That mindset shapes treatment in subtle ways. You might accept
                partial improvement as the best you can hope for. You might not
                push back when a medication only helps a little. You might not
                ask about newer options because, on some level, you've already
                decided the ceiling is low.
              </p>
              <p>
                If any of that lands, notice it. Your family history explains
                why you got depressed. It doesn't get to decide how far you
                recover.
              </p>

              {/* Infographic: When to pay closer attention */}
              <div className="my-6 overflow-hidden rounded-2xl border border-border bg-card shadow-soft not-prose">
                <img
                  src="https://vibe.filesafe.space/1787327131284421342/attachments/7b3d45f0-b380-421c-860d-49051aefccd4.png"
                  alt="When to pay closer attention: Persistent low mood, loss of interest, sleep changes, low energy, irritability, trouble concentrating"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          ),
        },
        {
          heading: "Inherited depression is often circuit-level depression",
          body: (
            <div className="space-y-4">
              <p>
                Here's something that reframes the whole question. Depression
                that runs strongly in families tends to be the recurrent,
                biologically driven kind: it comes back, it doesn't always track
                with life events, and it often responds only partially to
                medication. That pattern points to something happening in the
                brain's wiring, not just its chemistry.
              </p>
              <p>
                Brain imaging consistently shows that in depression, the region
                behind the forehead called the dorsolateral prefrontal cortex is
                underactive. It regulates mood, motivation, and attention, and
                its connections to the rest of the mood network are weakened.
                Antidepressants change the chemical environment around that
                circuit. They don't directly retrain it, which is why someone
                with a strong family history can be on a high dose of a good
                medication and still feel fundamentally unchanged.
              </p>
              <p>
                Transcranial magnetic stimulation (TMS) works on the circuit
                itself. It uses focused magnetic pulses to stimulate that
                underactive region directly, with no anesthesia and nothing
                entering your bloodstream. It doesn't care whether your
                depression came from genes or circumstance. It works on the
                network that's underperforming, wherever the underperformance
                came from.
              </p>
            </div>
          ),
        },
        {
          heading: "What SAINT adds",
          body: (
            <div className="space-y-4">
              <div className="my-6 grid gap-4 sm:grid-cols-2 not-prose">
                <div className="overflow-hidden rounded-xl border border-border bg-card shadow-soft">
                  <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
                    <img
                      src="https://vibe.filesafe.space/1787327131284421342/attachments/61fd8a15-3767-464e-99b6-5a1351dea1b3.png"
                      alt="Personalized fMRI brain scan targeting for SAINT® TMS therapy"
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-3.5">
                    <p className="font-headings text-sm font-bold text-primary">
                      Personalized fMRI Targeting
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                      Maps your own unique neural connectivity rather than
                      relying on standard external head markers.
                    </p>
                  </div>
                </div>

                <div className="overflow-hidden rounded-xl border border-border bg-card shadow-soft">
                  <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
                    <img
                      src="https://vibe.filesafe.space/1787327131284421342/attachments/9907b0c5-c62d-4096-ba03-9d7a9cc3ffc5.png"
                      alt="Patient seated comfortably in SAINT® treatment suite during stimulation series"
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-3.5">
                    <p className="font-headings text-sm font-bold text-primary">
                      Five-Day Stimulation Series
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                      Five clinic days, ten 10-minute sessions daily—targeted to
                      retrain underactive mood circuits.
                    </p>
                  </div>
                </div>
              </div>

              <p>
                SAINT (Stanford Accelerated Intelligent Neuromodulation Therapy)
                is an accelerated, FDA-cleared form of TMS developed at
                Stanford's Brain Stimulation Lab. It's indicated for adults with
                major depressive disorder who haven't gotten adequate relief
                from antidepressants in their current episode, which describes a
                large share of people with inherited, recurrent depression.
              </p>
              <p>
                What makes it especially relevant is that no two brains are
                wired the same way, even within a family. Standard TMS locates
                its target using external measurements of the head. SAINT uses a
                functional MRI (fMRI) of your own brain. Here's how it works at
                a{" "}
                <Link
                  to="/find-a-verified-saint-provider"
                  className="font-bold text-accent underline underline-offset-2 hover:text-accent/80"
                >
                  Verified SAINT® Provider
                </Link>{" "}
                like Neuro Wellness TMS Centers of America:
              </p>
              <ol className="list-decimal pl-5 space-y-2">
                <li>
                  <strong>Consultation (Zoom web conference).</strong> You meet
                  with our clinical team over a convenient Zoom call. As your
                  evaluation evolves, you will also have the option to meet with
                  our psychiatrist by Zoom web conference or in person at our
                  clinic. You meet with our clinical team to review your
                  symptoms, your medication history, your family history, and
                  whether SAINT is right for you.
                </li>
                <li>
                  <strong>fMRI brain scan (mandatory in person).</strong> A
                  roughly 45-minute functional MRI maps how your individual
                  brain networks are wired and where they're communicating
                  poorly. (Mandatory in-person scan at an imaging partner
                  center.)
                </li>
                <li>
                  <strong>Personalized targeting.</strong> A targeting algorithm
                  reads your scan and identifies the exact location where
                  stimulation will most effectively reach the mood-regulation
                  network. Neuronavigation guides the coil to that spot for
                  every session.
                </li>
                <li>
                  <strong>
                    Five-day stimulation series (mandatory in person).
                  </strong>{" "}
                  In-person treatment Monday through Friday at our Coral Springs
                  clinic. Ten 10-minute sessions per day, with 50-minute breaks
                  in between, for five consecutive days. Five clinic visits,
                  compared with 30 to 36 for conventional TMS. Most patients
                  notice a change in mood within the first couple of days.
                </li>
              </ol>

              {/* Private Suites Callout */}
              <div className="overflow-hidden rounded-xl border border-border bg-card shadow-soft not-prose my-6">
                <div className="relative aspect-[16/9] w-full overflow-hidden bg-muted">
                  <img
                    src="https://vibe.filesafe.space/1787327131284421342/attachments/65bd3a1f-5ba1-43da-8b0f-00417c5d48ed.png"
                    alt="Private furnished suite in dedicated SAINT Lounge Wing with leather recliner, sectional, workstation, and refreshment bar"
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="p-4">
                  <p className="font-headings text-sm font-bold text-primary">
                    Private Suites &amp; Dedicated SAINT® Lounge Wing
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                    Designed as a restful outpatient retreat. Between sessions,
                    you and your family have your own private suite with
                    recliners, workspace, and refreshments. Our on-site care
                    team attends to you throughout the day so you have lunch in
                    comfort and feel completely supported at home.
                  </p>
                </div>
              </div>
            </div>
          ),
        },
        {
          heading: "What the results look like",
          body: (
            <div className="space-y-4">
              <SaintComparisonVisualSplit />
              <p>
                In the clinical trials behind SAINT's FDA clearance,
                participants had treatment-resistant depression and had already
                failed prior treatments. Seventy-nine percent reached remission,
                not just improvement, with an average time to remission of about
                two and a half days. Across more than 400 trial patients, 85
                percent experienced symptom relief, with no serious adverse
                events.
              </p>
              <p>
                Side effects were generally mild: headache, fatigue, and
                temporary scalp discomfort at the treatment site. SAINT isn't
                appropriate for people with non-removable metal in or near the
                head or an implanted device, and you'll be screened for these at
                your consultation.
              </p>
              <p>
                You don't have to stop your current medications. Most patients
                continue their existing regimen through treatment.
              </p>
            </div>
          ),
        },
        {
          heading: "A different way to think about it",
          body: (
            <div className="space-y-4">
              <p>
                If depression runs in your family, that's not a life sentence.
                It's a reason to take your own treatment seriously and to pursue
                the most effective options available, rather than settling for
                the ones that helped your parents get by.
              </p>
              <p>
                You may also be the first person in your family with access to
                treatments that didn't exist a generation ago. A five-day,
                imaging-guided course of brain stimulation wasn't an option for
                your mother. It is for you. That's worth something.
              </p>

              {/* Infographic: How support and treatment can look */}
              <div className="my-6 overflow-hidden rounded-2xl border border-border bg-card shadow-soft not-prose">
                <img
                  src="https://vibe.filesafe.space/1787327131284421342/attachments/ca34a2bd-9b4d-40ae-a51b-55f2e2e0fc8b.png"
                  alt="How support and treatment can look: Evaluation, therapy/medication review, TMS options, SAINT when appropriate, follow-up care"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          ),
        },
        {
          heading: "What to do next",
          body: (
            <div className="space-y-4">
              <p>
                If you've been quietly assuming that your depression is just
                part of your inheritance, we'd like to challenge that assumption
                with you.
              </p>

              {/* Photo: Talking with a provider can help you understand the full picture */}
              <div className="my-6 overflow-hidden rounded-2xl border border-border bg-card shadow-soft not-prose">
                <img
                  src="https://vibe.filesafe.space/1787327131284421342/attachments/da7f27cb-2fb9-44f8-815d-1be829044c41.png"
                  alt="Doctor consulting with a patient: Talking with a provider can help you understand the full picture"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
              </div>

              <p>
                At Neuro Wellness TMS Centers of America, the first appointment
                is an evaluation, not a commitment. We'll start with your
                history, including your family history, and end with a clear
                opinion about whether SAINT is a good fit. If it is, we'll walk
                you through the scan, the schedule, and what to expect during
                your treatment week. Insurance coverage for SAINT varies by
                plan; our team can help you understand your options, including
                prior authorization and self-pay.
              </p>
              <p className="font-semibold text-primary">
                Schedule a consultation:{" "}
                <a
                  href="tel:9548279793"
                  className="text-primary hover:underline"
                >
                  954-827-9793
                </a>
              </p>
              <p className="text-xs text-muted-foreground italic pt-2">
                If you are in crisis or thinking about harming yourself, please
                call or text 988 (Suicide and Crisis Lifeline) or go to your
                nearest emergency room. This article is for educational purposes
                and is not a substitute for individual medical advice.
              </p>
            </div>
          ),
        },
      ]}
      faq={faqs}
      relatedLinks={related}
      finalCtaText="Schedule a Consultation"
    />
  );
}
